import { injectable } from 'tsyringe';
import type { PermissionEntry } from '@bmai/auth-api-client';
import {
    PERMISSION_CATALOG,
    permissionResourceLabel,
    type PermissionGroup,
    type PermissionResourceMeta,
} from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { RolesAdminService, type RoleView, type RoleGrant } from './RolesAdminService';

interface CatalogGroup {
    group: PermissionGroup;
    label: string;
    resources: PermissionResourceMeta[];
}

const CATALOG_GROUPS: readonly { group: PermissionGroup; label: string }[] = [
    { group: 'features', label: 'Features' },
    { group: 'administration', label: 'Administration' },
];

@injectable()
export class RolesTabViewModel extends BaseViewModel {
    roles: RoleView[] | null = null;
    allPermissions: PermissionEntry[] | null = null;
    loading: boolean = true;

    modalOpen: boolean = false;
    selectedRoleId: string | null = null;
    roleName: string = '';
    roleDesc: string = '';
    grants: Record<string, string[]> = {};
    saving: boolean = false;
    permissionSearch: string = '';
    deleteTarget: RoleView | null = null;

    constructor(private service: RolesAdminService) {
        super();
    }

    get selectedRole(): RoleView | null {
        return this.roles?.find((r) => r.id === this.selectedRoleId) ?? null;
    }

    /** True when the draft grants differ from the selected role's saved permissions. */
    get dirty(): boolean {
        const role = this.selectedRole;
        if (!role) return false;
        const draft = new Map<string, Set<string>>();
        for (const [res, scopes] of Object.entries(this.grants)) if (scopes.length > 0) draft.set(res, new Set(scopes));
        const saved = new Map<string, Set<string>>();
        for (const g of role.permissions) saved.set(g.resource, new Set(g.scopes));
        if (draft.size !== saved.size) return true;
        for (const [res, set] of draft) {
            const s = saved.get(res);
            if (!s || s.size !== set.size) return true;
            for (const a of set) if (!s.has(a)) return true;
        }
        return false;
    }

    get resources(): string[] {
        return PERMISSION_CATALOG.map((r) => r.resource);
    }

    /** Human label for a permission resource (e.g. `data_retention` → "Data & Retention"). */
    resourceLabel(resource: string): string {
        return permissionResourceLabel(resource);
    }

    /**
     * The canonical feature-aligned permission catalog as `{resource, actions}` —
     * sourced from `@bmai/shared` so the editor only offers real, valid actions.
     */
    get resourceActions(): { resource: string; actions: string[] }[] {
        return PERMISSION_CATALOG.map((r) => ({ resource: r.resource, actions: [...r.actions] }));
    }

    /** The catalog grouped (Features / Administration) for the editor, filtered by search. */
    get filteredCatalogGroups(): CatalogGroup[] {
        const q = this.permissionSearch.trim().toLowerCase();
        return CATALOG_GROUPS.map((meta) => {
            const resources = PERMISSION_CATALOG.filter(
                (r) =>
                    r.group === meta.group &&
                    (q === '' ||
                        r.resource.toLowerCase().includes(q) ||
                        r.label.toLowerCase().includes(q) ||
                        r.actions.some((a) => a.includes(q))),
            );
            return { group: meta.group, label: meta.label, resources };
        }).filter((g) => g.resources.length > 0);
    }

    /** Total granted (resource, action) pairs in the current draft. */
    get grantedCount(): number {
        return Object.values(this.grants).reduce((n, scopes) => n + scopes.length, 0);
    }

    setPermissionSearch(value: string): void {
        this.permissionSearch = value;
        this.notifyChange();
    }

    private validActions(resource: string): string[] {
        return this.resourceActions.find((g) => g.resource === resource)?.actions ?? [];
    }

    isResourceFullyGranted(resource: string): boolean {
        const valid = this.validActions(resource);
        const current = this.grants[resource] ?? [];
        return valid.length > 0 && valid.every((a) => current.includes(a));
    }

    isResourcePartiallyGranted(resource: string): boolean {
        const valid = this.validActions(resource);
        const granted = valid.filter((a) => (this.grants[resource] ?? []).includes(a)).length;
        return granted > 0 && granted < valid.length;
    }

    /** Grant all of a resource's valid actions, or clear them if already all granted. */
    toggleResourceAll(resource: string): void {
        const valid = this.validActions(resource);
        this.grants = { ...this.grants, [resource]: this.isResourceFullyGranted(resource) ? [] : [...valid] };
        this.notifyChange();
    }

    openDelete(role: RoleView): void {
        if (role.isSystem) return;
        this.deleteTarget = role;
        this.notifyChange();
    }

    closeDelete(): void {
        this.deleteTarget = null;
        this.notifyChange();
    }

    async confirmDelete(): Promise<{ success: boolean; name: string; error?: string }> {
        const role = this.deleteTarget;
        if (!role) return { success: false, name: '', error: 'No role selected' };
        const result = await this.deleteRole(role);
        this.deleteTarget = null;
        this.notifyChange();
        return { ...result, name: role.name };
    }

    async load(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            const [roles, perms] = await Promise.all([this.service.listRoles(), this.service.listPermissions()]);
            this.roles = roles;
            this.allPermissions = perms;
            const keep = this.selectedRoleId ? roles.find((r) => r.id === this.selectedRoleId) : null;
            const sel = keep ?? roles[0] ?? null;
            this.selectedRoleId = sel?.id ?? null;
            this.grants = sel ? this.initGrants(sel.permissions) : {};
        } catch {
            this.roles = null;
            this.allPermissions = null;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    openCreate(): void {
        this.roleName = '';
        this.roleDesc = '';
        this.modalOpen = true;
        this.notifyChange();
    }

    closeModal(): void {
        this.modalOpen = false;
        this.notifyChange();
    }

    /** Select a role for the detail pane and seed its editable permission draft. */
    selectRole(id: string): void {
        const role = this.roles?.find((r) => r.id === id) ?? null;
        if (!role) return;
        this.selectedRoleId = id;
        this.grants = this.initGrants(role.permissions);
        this.permissionSearch = '';
        this.notifyChange();
    }

    /** Discard unsaved permission edits, restoring the selected role's saved grants. */
    resetDraft(): void {
        const role = this.selectedRole;
        this.grants = role ? this.initGrants(role.permissions) : {};
        this.notifyChange();
    }

    setRoleName(value: string): void {
        this.roleName = value;
        this.notifyChange();
    }

    setRoleDesc(value: string): void {
        this.roleDesc = value;
        this.notifyChange();
    }

    toggleScope(resource: string, scope: string): void {
        const current = this.grants[resource] ?? [];
        const next = current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope];
        this.grants = { ...this.grants, [resource]: next };
        this.notifyChange();
    }

    isScopeGranted(resource: string, scope: string): boolean {
        return this.grants[resource]?.includes(scope) ?? false;
    }

    /** Create a new (empty) custom role from the modal, then select it for editing. */
    async createRole(): Promise<{ success: boolean; name: string; error?: string }> {
        if (!this.roleName.trim()) return { success: false, name: '', error: 'Role name required' };
        this.saving = true;
        this.notifyChange();
        const name = this.roleName.trim().toLowerCase();
        try {
            await this.service.createRole({ name, description: this.roleDesc });
            const refreshed = await this.service.listRoles();
            this.roles = refreshed;
            const created = refreshed.find((r) => r.name === name) ?? null;
            this.selectedRoleId = created?.id ?? this.selectedRoleId;
            this.grants = created ? this.initGrants(created.permissions) : this.grants;
            this.modalOpen = false;
            return { success: true, name };
        } catch (e) {
            return { success: false, name, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    /** Persist the selected role's edited permission grants. */
    async savePermissions(): Promise<{ success: boolean; error?: string }> {
        if (!this.selectedRoleId) return { success: false, error: 'No role selected' };
        this.saving = true;
        this.notifyChange();
        try {
            const grantsArray = Object.entries(this.grants)
                .filter(([, scopes]) => scopes.length > 0)
                .map(([resource, scopes]) => ({ resource, scopes }));
            await this.service.assignRolePermissions({ id: this.selectedRoleId, grants: grantsArray });
            const refreshed = await this.service.listRoles();
            this.roles = refreshed;
            const sel = refreshed.find((r) => r.id === this.selectedRoleId) ?? null;
            this.grants = sel ? this.initGrants(sel.permissions) : this.grants;
            return { success: true };
        } catch (e) {
            return { success: false, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async deleteRole(role: RoleView): Promise<{ success: boolean; error?: string }> {
        if (role.isSystem) return { success: false, error: 'System roles cannot be deleted' };
        try {
            await this.service.deleteRole(role.id);
            void this.load();
            return { success: true };
        } catch (e) {
            return { success: false, error: (e as Error).message };
        }
    }

    private initGrants(existingGrants?: RoleGrant[]): Record<string, string[]> {
        const initial: Record<string, string[]> = {};
        for (const r of this.resources) initial[r] = [];
        if (existingGrants) {
            for (const g of existingGrants) {
                if (initial[g.resource] !== undefined) initial[g.resource] = [...g.scopes];
            }
        }
        return initial;
    }

}
